import BaseScene from 'src/scenes/base';
import Transitions from 'src/fx/transition/transitions';
import { Themes, ThemeKey } from 'src/themes-v2';
import { nanoid } from 'nanoid';
import { AchievementKey } from './game.types';


/**
 * ------------------------------------------------------------------------------------------
 * Utility Types
 * ------------------------------------------------------------------------------------------
 */
type ValueOf<T> = T[keyof T];
type NoArray<T> = T extends any[] ? never : T;
type ArrayElement<ArrayType extends readonly unknown[]> = 
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;


/**
 * ------------------------------------------------------------------------------------------
 * Scene / Transitions
 * ------------------------------------------------------------------------------------------
 */
enum Scene {
    PRELOAD = "Preload",
    MENU = "Menu",
    GAME = "Game",
    POSTGAME = 'PostGame',
    RESTART_GAME = 'RestartGame',
    INTERSTITIAL_SCENE = 'InterstitialScene'
}

enum SceneState {
    PRELOAD,
    CREATE,
    UPDATE
}


type Direction = 'left' | 'bottom' | 'right' | 'top';
type SceneTransitionName = keyof typeof Transitions['Scene'];
type AudioTransitionName = keyof typeof Transitions['Audio'];
type MenuTransitionName = keyof typeof Transitions['Menu'];
interface SceneTransitionConfig {
    enter?: SceneTransitionName | {
        name: SceneTransitionName,
        duration?: number
    },
    exit?: SceneTransitionName | {
        name: SceneTransitionName,
        duration?: number
    }
}

interface AudioTransitionConfig {
    enter?: AudioTransitionName | {
        name: AudioTransitionName,
        duration?: number
    },
    exit?: AudioTransitionName | {
        name: AudioTransitionName,
        duration?: number
    }
}

interface MenuTransitionConfig {
    enter?: MenuTransitionName | {
        name: MenuTransitionName,
        duration?: number
    },
    exit?: MenuTransitionName | {
        name: MenuTransitionName,
        duration?: number
    }
}

interface BaseTransitionConfig {
    duration?: number,
    [key:string]: any
}

abstract class BaseTransition {
    protected readonly scene: BaseScene;
    protected readonly config: BaseTransitionConfig;
    constructor(scene: BaseScene, config: BaseTransitionConfig = {}){
        this.scene = scene;
        this.config = config;
    }

    abstract enter(onComplete?: Function) : Promise<void> | undefined
    abstract exit(onComplete?: Function) : Promise<void> | undefined
    abstract cancel() : void
    abstract destroy() : void
}

abstract class SceneTransition extends BaseTransition {
    protected id: string = nanoid();
    constructor(scene: BaseScene, config: BaseTransitionConfig = {}){
        super(scene, config);
    }
}

abstract class AudioTransition extends BaseTransition {
    protected track: Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound;
    constructor(scene: BaseScene, track: Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound, config: BaseTransitionConfig = {}){
        super(scene, config);
        this.track = track;
    }

    destroy(){
        setTimeout(() => {
            this.track.destroy();
        }, this.config.duration);
    }
}

abstract class MenuTransition {
    protected readonly config: BaseTransitionConfig;
    protected css: string = '';
    constructor(config: BaseTransitionConfig = {}, css: string){
        this.css = css;
        this.config = config;
        let styleEl = document.getElementById(this.constructor.name);
        if (!styleEl){
            document.body.insertAdjacentHTML('beforeend', `
                <style id="${this.constructor.name}">
                    ${this.css}
                </style>
            `);
        }
    }

    getClassName(){
        return this.constructor.name ?? '';
    }
}

interface BaseSceneTransitionConfig {
    scene?: SceneTransitionConfig,
    audio?: AudioTransitionConfig,
    menu?: MenuTransitionConfig
}

interface BaseSceneConfig extends Phaser.Types.Scenes.SettingsConfig {
    key: Scene,
    trackKey?: string,
    transition?: BaseSceneTransitionConfig
}




interface AudioSettings {
    muted: boolean
}

// type ThemeKey = keyof typeof Themes;
type Theme = typeof Themes[ThemeKey];


/**
 * ------------------------------------------------------------------------------------------
 * Stats & Achievements
 * ------------------------------------------------------------------------------------------
 */

type Unlockables = { [key:string]: Unlockable }
type UnlockableRewardTypeKey = keyof Unlockable['reward'];
type Unlockable = {
    achievements: AchievementKey[],
    credits: number,
    reward: {
        theme?: ThemeKey,
        //audioPack?: AudioPackKey //For future, can add more rewards like this
    }
}

enum AchievementProgressFormat {
    COUNT = 'count',
    BOOLEAN = 'boolean'
}

type AchievementProgress = {
    format: AchievementProgressFormat.COUNT,
    value: number,
    total: number
} | {
    format: AchievementProgressFormat.BOOLEAN,
    value: boolean
}

/**
 * Defines the structure for an achievement, including a function which is used to test for
 * completion.
 */
type Achievement <AchievementKey extends string, AchievementCrossGameStats, AchievementInGameStats> = {
    name: string,
    description: string,
    credits: number,
    test: (data: AchievementCrossGameStats, stats: AchievementInGameStats) => boolean,
    progress: ( data: AchievementCrossGameStats, stats: AchievementInGameStats, completed: AchievementKey[]) => AchievementProgress,
}

/**
 * A key/value object registry for all the Achievements for the game. This is passed into the
 * AchievementManager on creation.
 */
// type AchievementRegistry <Key extends string, AchievementCrossGameStats, AchievementInGameStats> = {
//     [key in Key] : Achievement<Key, AchievementCrossGameStats, AchievementInGameStats>
// }


type SimplePersistable = NoArray<{ [key:string]: string | number | boolean | any[] }>;

type HEX = `#${string}`;

export {
    ValueOf,
    NoArray,
    ArrayElement,
    Direction,
    Scene,
    SceneState,
    SceneTransitionName,
    AudioTransitionName,
    MenuTransitionName,
    BaseTransitionConfig,
    BaseTransition,
    SceneTransition,
    AudioTransition,
    MenuTransition,
    BaseSceneTransitionConfig,
    SceneTransitionConfig,
    AudioTransitionConfig,
    MenuTransitionConfig,
    BaseSceneConfig,
    AudioSettings,
    ThemeKey,
    Theme,
    Unlockables,
    UnlockableRewardTypeKey,
    Unlockable,
    AchievementProgressFormat,
    AchievementProgress,
    Achievement,
    SimplePersistable,
    HEX
}